import React from 'react';
import { Box, IconButton, Tooltip, alpha } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { resolveDownloadUrl, triggerBrowserDownload, trackDirectDownload } from '../../utils/downloadResume';
import UserAvatar from './UserAvatar';

const iconBtnSx = {
  width: 36,
  height: 36,
  borderRadius: 1.5,
  color: 'text.secondary',
  '&:hover': {
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    color: 'text.primary',
  },
};

/**
 * Right-side AppBar actions: status, theme, refresh, account.
 */
const AppToolbarActions = ({
  isDark,
  onToggleMode,
  onRefresh,
  user,
  userMenuAnchor,
  onUserMenuOpen,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      p: 0.5,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: (theme) =>
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.04)
          : alpha(theme.palette.grey[100], 0.8),
    }}
  >
    <Tooltip title="All systems operational">
      <IconButton size="small" aria-label="System status" sx={{ ...iconBtnSx, color: 'success.main' }}>
        <CheckCircleIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
      <IconButton size="small" onClick={onToggleMode} aria-label="Toggle theme" sx={iconBtnSx}>
        {isDark ? <LightModeIcon sx={{ fontSize: 20 }} /> : <DarkModeIcon sx={{ fontSize: 20 }} />}
      </IconButton>
    </Tooltip>

    <Tooltip title="Refresh data">
      <IconButton size="small" onClick={onRefresh} aria-label="Refresh" sx={iconBtnSx}>
        <RefreshIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Download Candor IDE">
      <IconButton
        size="small"
        onClick={async () => {
          try {
            const link = await resolveDownloadUrl();
            triggerBrowserDownload(link);
            trackDirectDownload(user, link, 'toolbar');
          } catch (err) {
            console.error('Download failed:', err);
          }
        }}
        aria-label="Download Candor IDE"
        sx={{ ...iconBtnSx, color: 'emerald.main' || 'success.main' }}
      >
        <CloudDownloadIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>

    <Box
      sx={{
        width: '1px',
        height: 24,
        bgcolor: 'divider',
        mx: 0.25,
      }}
    />

    <Tooltip
      title={
        user?.name
          ? `${user.name}${user?.email ? ` · ${user.email}` : ''}`
          : 'Account'
      }
    >
      <IconButton
        onClick={onUserMenuOpen}
        aria-haspopup="true"
        aria-expanded={Boolean(userMenuAnchor)}
        aria-label="Account menu"
        sx={{
          p: 0.35,
          borderRadius: 2,
          border: 1,
          borderColor: userMenuAnchor ? 'primary.main' : 'transparent',
          bgcolor: userMenuAnchor
            ? (theme) => alpha(theme.palette.primary.main, 0.1)
            : 'transparent',
          '&:hover': {
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <UserAvatar
          user={user}
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.8rem',
            fontWeight: 700,
            background: 'linear-gradient(145deg, #2563eb 0%, #0d9488 100%)',
          }}
        />
      </IconButton>
    </Tooltip>
  </Box>
);

export default AppToolbarActions;
